/**
 * useReports — данные дашбордов рекламы (read-only).
 *
 * Источник правды — скиллы yandex-direct / yandex-metrika (см. server/src/reports/*).
 * Бэкенд агрегирует CPL кросс-счётчиково (цели 477104111 + 519137685 + 457796506)
 * и помечает call.komma.software как слепую зону (нет счётчика).
 *
 * Контракты ниже = контракт REST API сервера.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { type Period, periodQuery, periodKey } from '@/components/ui/PeriodPicker';

export interface ReportPeriod {
  from: string;
  to: string;
  days: number;
}

export interface DomainBreakdown {
  domain: string;
  spend: number;
  /** null — слепая зона (нет счётчика). */
  leads: number | null;
  cpl: number | null;
  tracked: boolean;
  note?: string;
}

export type DataSource = 'live' | 'stub';

export interface OverviewResponse {
  source: DataSource;
  period: ReportPeriod;
  kpis: {
    cpl: number | null;
    spend: number;
    leads: number;
    activeCampaigns: number;
    /** Красная линия CPL. */
    cplCap: number;
  };
  money: {
    /** Сожжённый бюджет: активные tracked-кампании с 0 заявок. */
    burned: number;
    /** Сколько лидов можно добрать перераспределением (burned / CPL лидера). */
    potentialLeads: number;
    leaderCpl: number;
  };
  trend: { date: string; cpl: number | null; spend: number; leads: number }[];
  domains: DomainBreakdown[];
  clusters: ClusterBreakdown[];
}

export interface ClusterBreakdown {
  cluster: string;
  spend: number;
  leads: number;
  cpl: number | null;
}

export type CampaignStatus = 'ON' | 'OFF' | 'SUSPENDED' | 'ARCHIVED' | 'UNKNOWN';

export interface CampaignRow {
  id: number;
  name: string;
  domain: string;
  status: CampaignStatus;
  spend: number;
  leads: number | null;
  cpl: number | null;
  tracked: boolean;
}

export interface CampaignsResponse {
  source: DataSource;
  period: ReportPeriod;
  campaigns: CampaignRow[];
}

export type AnomalySeverity = 'high' | 'medium' | 'low';

export interface Anomaly {
  id: string;
  severity: AnomalySeverity;
  type: string;
  title: string;
  detail: string;
  campaign?: string;
  campaignId?: number;
  domain?: string;
}

export interface AnomaliesResponse {
  generatedAt: string;
  anomalies: Anomaly[];
}

export interface SearchTerm {
  query: string;
  cost: number;
  clicks: number;
  leads: number;
}
export interface SearchTermsResponse {
  source: DataSource;
  terms: SearchTerm[];
}

const reportKeys = {
  overview: (p: Period) => ['reports', 'overview', periodKey(p)] as const,
  campaigns: (p: Period) => ['reports', 'campaigns', periodKey(p)] as const,
  anomalies: () => ['reports', 'anomalies'] as const,
  searchTerms: (p: Period) => ['reports', 'search-terms', periodKey(p)] as const,
};

export function useOverview(period: Period) {
  return useQuery<OverviewResponse>({
    queryKey: reportKeys.overview(period),
    queryFn: () => apiFetch<OverviewResponse>(`/api/reports/overview?${periodQuery(period)}`),
    staleTime: 60_000,
    placeholderData: keepPreviousData, // смена периода не сбрасывает страницу в лоадер
  });
}

export function useCampaigns(period: Period) {
  return useQuery<CampaignsResponse>({
    queryKey: reportKeys.campaigns(period),
    queryFn: () => apiFetch<CampaignsResponse>(`/api/reports/campaigns?${periodQuery(period)}`),
    staleTime: 60_000,
    placeholderData: keepPreviousData, // смена периода не сбрасывает страницу в лоадер
  });
}

export function useAnomalies() {
  return useQuery<AnomaliesResponse>({
    queryKey: reportKeys.anomalies(),
    queryFn: () => apiFetch<AnomaliesResponse>('/api/reports/anomalies'),
    staleTime: 60_000,
    placeholderData: keepPreviousData, // смена периода не сбрасывает страницу в лоадер
  });
}

export function useSearchTerms(period: Period) {
  return useQuery<SearchTermsResponse>({
    queryKey: reportKeys.searchTerms(period),
    queryFn: () => apiFetch<SearchTermsResponse>(`/api/reports/search-terms?${periodQuery(period)}`),
    staleTime: 60_000,
    placeholderData: keepPreviousData, // смена периода не сбрасывает страницу в лоадер
  });
}
